import { useEffect, useRef, useState } from 'react';
import { pickColorTier, type TimePressedColorTier } from './timePressedColor';

/**
 * TimePressedTimer — compte à rebours visible pour le mode
 * `tactic_drill_time_pressed` (MVP2.6 Gap 1).
 *
 * Pourquoi : Léon a un gap Lichess Puzzle 2144 vs Blitz 1949 (~195 pts).
 * Le levier #1 pour fermer ce gap est l'entraînement sous contrainte
 * temporelle (forcer la vitesse d'exécution de motifs déjà connus). Ce
 * composant matérialise la contrainte 30 s/puzzle.
 *
 * Design (cf. DESIGN.md §5, ton Ghost cyan) :
 *  - Barre de progression cyan-500 par défaut.
 *  - Vire ambre à 10 s restantes (warning sobre, pas anxiogène).
 *  - Vire coral à 5 s restantes (alerte ultime).
 *  - Pas de clignotement / blink — `prefers-reduced-motion` strictement
 *    respecté : la transition de couleur reste graduelle quand l'utilisateur
 *    ne réduit pas les motions, sinon palier net.
 *
 * Comportement :
 *  - Affiche le temps restant en secondes (1 décimale sous 10 s).
 *  - Appelle `onTimeout()` exactement une fois quand le temps écoulé
 *    dépasse `durationMs`.
 *  - `isPaused` (optionnel) suspend le compte à rebours (utilisé pendant
 *    l'animation pré-drill `InContextIntro` par exemple).
 *
 * Notes implémentation :
 *  - `requestAnimationFrame` plutôt que `setInterval` : pas de drift à long
 *    terme, et iOS Safari ne throttle pas le rAF dans le tab actif (le bug
 *    A2 touchstart vs setTimeout doc dans `tacticsDrillReducer.ts` ne s'applique
 *    pas ici car le timer n'est pas piloté par un touch event).
 *  - `performance.now()` pour la mesure d'elapsed (monotone, pas affecté par
 *    NTP sync).
 */

export interface TimePressedTimerProps {
  /** Durée totale du compte à rebours, en millisecondes. Typique : 30000. */
  durationMs: number;
  /**
   * Callback déclenché EXACTEMENT une fois quand `elapsed >= durationMs`.
   * Le parent doit dispatcher `submitAttempt({ result: 'fail',
   * time_taken_ms: durationMs, time_pressure: true })` à ce moment.
   */
  onTimeout: () => void;
  /**
   * Optionnel — si `true`, fige le compte à rebours sur place. Utilisé
   * notamment pendant l'animation pré-drill (`InContextIntro`) où Léon
   * regarde 10 coups défiler et ne devrait pas voir son temps tomber.
   */
  isPaused?: boolean;
}

const TIER_COLOR: Record<TimePressedColorTier, string> = {
  normal: 'var(--cyan-500)',
  warning: 'var(--amber-500)',
  critical: 'var(--coral-500)',
};

const TIER_TEXT_CLASS: Record<TimePressedColorTier, string> = {
  normal: 'text-cyan-500',
  warning: 'text-amber-500',
  critical: 'text-coral-500',
};

export function TimePressedTimer({
  durationMs,
  onTimeout,
  isPaused = false,
}: TimePressedTimerProps): JSX.Element {
  const [elapsedMs, setElapsedMs] = useState(0);
  const startRef = useRef<number | null>(null);
  // Total elapsed time spent paused, soustrait du `now - start` pour ne pas
  // brûler du temps pendant la pause.
  const pausedAccumulatedRef = useRef<number>(0);
  // Marque le moment où la pause commence pour calculer l'incrément à
  // ajouter à `pausedAccumulatedRef` quand on reprend.
  const pauseStartRef = useRef<number | null>(null);
  const calledTimeoutRef = useRef<boolean>(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    function tick(): void {
      const now = performance.now();
      if (startRef.current === null) {
        startRef.current = now;
      }
      if (isPaused) {
        if (pauseStartRef.current === null) {
          pauseStartRef.current = now;
        }
        // En pause : on ne met pas à jour `elapsedMs`, juste on re-schedule.
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      // Sortie de pause : on accumule la durée pausée.
      if (pauseStartRef.current !== null) {
        pausedAccumulatedRef.current += now - pauseStartRef.current;
        pauseStartRef.current = null;
      }
      const elapsed = now - startRef.current - pausedAccumulatedRef.current;
      const clamped = Math.max(0, Math.min(durationMs, elapsed));
      setElapsedMs(clamped);
      if (elapsed >= durationMs && !calledTimeoutRef.current) {
        calledTimeoutRef.current = true;
        onTimeout();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // `onTimeout` est volontairement hors deps : on capture la callback de
    // mount. Si le parent veut changer le callback à chaud, qu'il `key` le
    // composant. C'est l'invariant attendu pour un timer one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationMs, isPaused]);

  const remainingMs = Math.max(0, durationMs - elapsedMs);
  const tier = pickColorTier(remainingMs);
  const progressPct = Math.max(0, Math.min(100, (remainingMs / durationMs) * 100));
  const remainingSeconds = remainingMs / 1000;
  const displaySeconds =
    remainingMs <= 10000 ? remainingSeconds.toFixed(1) : Math.ceil(remainingSeconds).toString();

  return (
    <div
      data-testid="time-pressed-timer"
      data-tier={tier}
      role="timer"
      aria-live="off"
      aria-label={`Temps restant : ${Math.ceil(remainingSeconds)} secondes`}
      className="flex flex-col gap-1.5 p-3 rounded-lg border border-ink-700 glass motion-safe:transition-colors motion-safe:duration-300"
    >
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-300">
          Sous pression
        </span>
        <span
          data-testid="time-pressed-remaining"
          className={`font-mono text-lg tabular-nums font-medium ${TIER_TEXT_CLASS[tier]}`}
        >
          {displaySeconds}s
        </span>
      </div>
      <div
        className="h-1.5 rounded-full bg-ink-900 overflow-hidden"
        aria-hidden="true"
      >
        <div
          data-testid="time-pressed-bar"
          className="h-full motion-safe:transition-[width,background-color] motion-safe:duration-150"
          style={{ width: `${progressPct}%`, backgroundColor: TIER_COLOR[tier] }}
        />
      </div>
    </div>
  );
}
